import React, { useEffect, useState } from 'react';
import { LogIn, LogOut, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../lib/firebase';
import { onIdTokenChanged } from 'firebase/auth';
import AuthModal from './AuthModal';

/**
 * CMS authentication gate.
 *
 * Uses Firebase's onIdTokenChanged listener so the stored token is always
 * current — the SDK refreshes ID tokens automatically ~5 min before expiry
 * and the listener writes the new value to localStorage immediately.
 *
 * A visibility-change handler also forces a refresh when the user returns
 * to a backgrounded tab, covering cases where setInterval would have been
 * throttled by the browser.
 */
const CMSAuth = ({ onAuthenticated }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------------
  // Core listener: fires on sign-in, sign-out, AND every automatic
  // token refresh. Keeps localStorage in sync at all times.
  // ------------------------------------------------------------------
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          localStorage.setItem('cms_token', token);
          onAuthenticated?.();
        } catch (e) {
          console.error('Failed to get Firebase ID token', e);
          setError('Could not retrieve auth token. Please sign in again.');
        }
      } else {
        localStorage.removeItem('cms_token');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // ------------------------------------------------------------------
  // Visibility handler: when the user returns to a tab that was
  // backgrounded for a long time, force-refresh the token immediately
  // so the first API call doesn't fail with a stale token.
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken(/* forceRefresh */ true);
          localStorage.setItem('cms_token', token);
        } catch (e) {
          console.error('Token refresh on tab focus failed', e);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Wanderloop CMS</h2>
          <p className="text-gray-500 mb-6">Sign in with your Wanderloop account to access the content management system.</p>

          {error && (
            <p className="text-sm text-red-600 mb-4">{error}</p>
          )}

          <button
            onClick={() => setShowAuthModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
          >
            <LogIn className="w-4 h-4" />
            Sign in
          </button>

          <p className="text-xs text-gray-400 mt-6">
            Only authorised Wanderloop accounts can access this area.
          </p>
        </div>

        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
          />
        )}
      </div>
    );
  }

  // Signed in — render a small user badge in the corner
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 flex items-center gap-3">
        <div className="text-right">
          <p className="text-xs font-medium text-gray-900">{user?.displayName || user?.email}</p>
          <p className="text-xs text-gray-500">CMS</p>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem('cms_token');
            logout();
            window.location.href = '/';
          }}
          className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
          title="Sign out"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CMSAuth;
